import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, Loader2, Search, RefreshCw } from "lucide-react";
import { Toast, ToastType, Badge, Button } from "../../../components/ui";
import { apiService } from "../../../services/api";
import { useAuth } from "../../../contexts/AuthContext";

type ConversationSummary = {
  phoneNumber: string;
  lastMessage?: string;
  lastMessageContent?: string;
  lastMessageAt: string;
  lastMessageType: string;
  lastMessageDirection: string;
  unreadCount: number;
  lead: {
    id: string;
    name: string;
    email?: string;
    status: string;
    assignedTo?: string | null;
  } | null;
};

export type InboxListFilter = "all" | "unread" | "today" | "mine";

interface WhatsAppConversationListProps {
  onSelectConversation: (phoneNumber: string, leadId?: string) => void;
  selectedPhoneNumber: string | null;
  listRefreshTrigger?: number;
  phoneNumberId?: string;
}

export const WhatsAppConversationList: React.FC<WhatsAppConversationListProps> = ({
  onSelectConversation,
  selectedPhoneNumber,
  listRefreshTrigger = 0,
  phoneNumberId,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [listFilter, setListFilter] = useState<InboxListFilter>("all");
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  useEffect(() => {
    const tmr = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(tmr);
  }, [searchInput]);

  const loadConversations = useCallback(
    async (isRefresh = false) => {
      try {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);
        const result = await apiService.getWhatsAppConversations({
          page: 1,
          limit: 100,
          search: debouncedSearch || undefined,
          phoneNumberId,
        });
        setConversations(result?.conversations || []);
      } catch {
        setToast({ message: t("whatsapp.inbox.errors.loadConversations"), type: "error" });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [debouncedSearch, phoneNumberId, t],
  );

  useEffect(() => {
    void loadConversations();
  }, [loadConversations, listRefreshTrigger]);

  useEffect(() => {
    const interval = setInterval(() => void loadConversations(true), 10000);
    return () => clearInterval(interval);
  }, [loadConversations]);

  const filteredConversations = useMemo(() => {
    let list = conversations;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    if (listFilter === "unread") {
      list = list.filter((c) => c.unreadCount > 0);
    } else if (listFilter === "today") {
      list = list.filter((c) => new Date(c.lastMessageAt).getTime() >= startOfToday.getTime());
    } else if (listFilter === "mine" && user?.id) {
      list = list.filter((c) => c.lead?.assignedTo === user.id);
    }
    return list;
  }, [conversations, listFilter, user?.id]);

  const formatTime = (dateStr?: string | null) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return t("whatsapp.inbox.now");
    if (diffMin < 60) return `${diffMin}min`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h`;
    return d.toLocaleDateString(i18n?.language || "pt-BR", { day: "2-digit", month: "2-digit" });
  };

  const previewText = (c: ConversationSummary) => c.lastMessage ?? c.lastMessageContent ?? "";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="p-3 border-b border-border space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">{t("whatsapp.inbox.conversations")}</h2>
          <button
            type="button"
            onClick={() => void loadConversations(true)}
            disabled={refreshing}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {(
            [
              ["all", t("whatsapp.inbox.filters.all")] as const,
              ["unread", t("whatsapp.inbox.filters.unread")] as const,
              ["today", t("whatsapp.inbox.filters.today")] as const,
              ["mine", t("whatsapp.inbox.filters.mine")] as const,
            ] as const
          ).map(([key, label]) => (
            <Button
              key={key}
              type="button"
              variant={listFilter === key ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs px-2"
              onClick={() => setListFilter(key)}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("whatsapp.inbox.searchPlaceholder")}
            className="w-full pl-9 pr-3 py-2 text-sm border border-input rounded-lg bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[200px] p-6 text-center">
            <MessageCircle className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              {searchInput || debouncedSearch ? t("whatsapp.inbox.noResults") : t("whatsapp.inbox.empty")}
            </p>
          </div>
        ) : (
          filteredConversations.map((conv) => (
            <button
              key={conv.phoneNumber}
              type="button"
              onClick={() => onSelectConversation(conv.phoneNumber, conv.lead?.id)}
              className={`w-full text-left p-3 border-b border-border transition-colors hover:bg-muted/50 ${
                selectedPhoneNumber === conv.phoneNumber ? "bg-muted" : ""
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate">
                      {conv.lead?.name || conv.phoneNumber}
                    </span>
                    {conv.unreadCount > 0 && (
                      <Badge
                        variant="danger"
                        className="text-xs px-1.5 py-0 h-5 min-w-5 flex items-center justify-center"
                      >
                        {conv.unreadCount}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{previewText(conv)}</p>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{formatTime(conv.lastMessageAt)}</span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};
